/**
 * Zonneplan-style day-ahead price view: stepped ct/kWh line over a
 * fixed window (~2 h ago through the last published bucket), 10 ct
 * gridlines, day boundaries as labelled rules, min/max badges, a "now"
 * dot and the shared scrub chip (data-swipe-consume, haptics). Input
 * is EUR/kWh (all-in or EPEX per the upstream toggle), drawn in cents.
 * docs/customer-app.md > Front-end > Price line
 */
import { useMemo, useRef, useState } from "react";

interface Props {
  t: number[];                    // bucket starts, unix seconds
  prices: (number | null)[];      // EUR/kWh, aligned with t
  currentIdx: number;
  /** Bucket seconds (900 for 15min, 3600 for 1h). */
  stepSec: number;
  /** A second, muted series on the same axis (the sell price on a
   *  market-price contract): grey stepped line under the main one,
   *  shown in the scrub readout, never badged. Stefan, 2026-10-04:
   *  one chart, the sell side greyed, not a second chart. */
  secondary?: (number | null)[] | null;
  secondaryLabel?: string;
  /** Buckets from this ts are the AI price forecast: same tiers, drawn
   *  lighter, like the Prognose backdrop (2026-10-30). */
  forecastFrom?: number | null;
}

const VB_W = 700;
const VB_H = 260;
const PAD = { left: 34, right: 10, top: 30, bottom: 30 };
const LOOKBACK_SEC = 2 * 3600;

// Cost tiers across the visible window (same quartile rule as the
// Prognose price backdrop): cheapest quartile green, most expensive
// quartile orange/red, midfield neutral.
const C_LOW  = "var(--color-price-low,  #22c55e)";
const C_MID  = "var(--color-grid,       #8a91a0)";
const C_HIGH = "var(--color-price-high, #ea580c)";

function fmtCt(eur: number): string {
  return `${(eur * 100).toFixed(2).replace(".", ",")} ct`;
}

export function PriceLine({
  t, prices, currentIdx, stepSec, secondary = null, secondaryLabel = "verkoop",
  forecastFrom = null,
}: Props) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const lastVibrated = useRef<number | null>(null);

  const view = useMemo(() => {
    // Window: a bit of the past for context, then everything known.
    let last = prices.length - 1;
    while (last >= 0 && prices[last] == null) last--;
    if (last < 0) return null;
    const nowTs = t[Math.min(currentIdx, t.length - 1)];
    let first = 0;
    while (first < last && t[first] < nowTs - LOOKBACK_SEC) first++;
    const idx: number[] = [];
    for (let i = first; i <= last; i++) idx.push(i);
    const vals = idx
      .map((i) => prices[i])
      .filter((v): v is number => v != null);
    if (!vals.length) return null;
    // the secondary series shares the axis: widen the range to fit it
    for (const i of idx) {
      const v = secondary?.[i];
      if (v != null) vals.push(v);
    }
    const maxCt = Math.max(...vals) * 100;
    const minCt = Math.min(...vals) * 100;
    // As begint NIET op 0 als alles positief is - anders verdrinkt het
    // dag-verschil in een lege onderband (Stefan 19-08). 5-ct
    // roostering rondom de echte min/max; bij negatieve prijzen blijft
    // de 0-lijn vanzelf in beeld ("je krijgt geld toe").
    const top = Math.ceil(maxCt / 5) * 5;
    let bottom = Math.floor(minCt / 5) * 5;
    if (top - bottom < 5) bottom = top - 5;   // altijd een leesbaar bereik
    return { idx, first, last, top, bottom };
  }, [t, prices, currentIdx, secondary]);

  if (!view) return null;
  const { idx, first, last, top, bottom } = view;

  const innerW = VB_W - PAD.left - PAD.right;
  const innerH = VB_H - PAD.top - PAD.bottom;
  const span = t[last] + stepSec - t[first];
  const x = (ts: number) => PAD.left + ((ts - t[first]) / span) * innerW;
  const y = (ct: number) =>
    PAD.top + (1 - (ct - bottom) / (top - bottom)) * innerH;

  // Cost tier per bucket - quartiles over the visible window.
  const windowVals = idx
    .map((i) => prices[i])
    .filter((v): v is number => v != null)
    .sort((a, b) => a - b);
  const q1 = windowVals[Math.floor(windowVals.length * 0.25)] ?? 0;
  const q3 = windowVals[Math.floor(windowVals.length * 0.75)] ?? 1;
  const tierOf = (p: number) => (p <= q1 ? 0 : p >= q3 ? 2 : 1);
  const tierColor = [C_LOW, C_MID, C_HIGH];

  // Stepped paths, one per tier, so segments carry their cost color.
  // Vertical connectors join in the color of the bucket being entered.
  // Forecast buckets go to their own set, drawn lighter.
  const tierSegs: string[][] = [[], [], []];
  const fcSegs: string[][] = [[], [], []];
  let prev: { x1: number; y: number } | null = null;
  for (const i of idx) {
    const p = prices[i];
    if (p == null) {
      prev = null;
      continue;
    }
    const ct = p * 100;
    const x0 = x(t[i]);
    const x1 = x(t[i] + stepSec);
    const yy = y(ct);
    const tier = tierOf(p);
    const segs = forecastFrom != null && t[i] >= forecastFrom ? fcSegs : tierSegs;
    if (prev) {
      // connector down/up into this bucket
      segs[tier].push(
        `M${prev.x1.toFixed(1)} ${prev.y.toFixed(1)} L${prev.x1.toFixed(1)} ${yy.toFixed(1)}`,
      );
    }
    segs[tier].push(
      `M${x0.toFixed(1)} ${yy.toFixed(1)} L${x1.toFixed(1)} ${yy.toFixed(1)}`,
    );
    prev = { x1, y: yy };
  }
  // Longer windows: fewer hour labels (every 6/12 h), beyond a week the
  // day rules carry the axis alone.
  const windowDays = span / 86400;
  const hourStep = windowDays <= 2.5 ? 3 : windowDays <= 4.5 ? 6 : windowDays <= 8 ? 12 : 24;
  const multiDay = span > 36 * 3600;

  // Secondary (sell price): one muted stepped path, no tiers.
  const secSegs: string[] = [];
  if (secondary) {
    let sprev: { x1: number; y: number } | null = null;
    for (const i of idx) {
      const p = secondary[i];
      if (p == null) {
        sprev = null;
        continue;
      }
      const x0 = x(t[i]);
      const x1 = x(t[i] + stepSec);
      const yy = y(p * 100);
      if (sprev) {
        secSegs.push(
          `M${sprev.x1.toFixed(1)} ${sprev.y.toFixed(1)} L${sprev.x1.toFixed(1)} ${yy.toFixed(1)}`,
        );
      }
      secSegs.push(`M${x0.toFixed(1)} ${yy.toFixed(1)} L${x1.toFixed(1)} ${yy.toFixed(1)}`);
      sprev = { x1, y: yy };
    }
  }

  // Min/max badges over the FUTURE part (now →) - that's the decision
  // info; fall back to the whole window when now sits at the end.
  const futureIdx = idx.filter((i) => i >= currentIdx && prices[i] != null);
  const badgePool = futureIdx.length >= 4 ? futureIdx : idx.filter((i) => prices[i] != null);
  let minI = badgePool[0];
  let maxI = badgePool[0];
  for (const i of badgePool) {
    if ((prices[i] as number) < (prices[minI] as number)) minI = i;
    if ((prices[i] as number) > (prices[maxI] as number)) maxI = i;
  }

  // Day boundaries inside the window.
  const days: { ts: number; label: string }[] = [];
  for (const i of idx) {
    const d = new Date(t[i] * 1000);
    if (d.getHours() === 0 && d.getMinutes() === 0) {
      const isTomorrow =
        new Date((t[currentIdx] ?? t[first]) * 1000).getDate() !== d.getDate() &&
        d.getTime() - (t[currentIdx] ?? t[first]) * 1000 < 36 * 3600 * 1000;
      days.push({
        ts: t[i],
        label: isTomorrow
          ? "morgen"
          : d.toLocaleDateString("nl-NL", { weekday: "short" }),
      });
    }
  }

  // Hour labels, every `hourStep` h (none beyond a week: day rules suffice).
  const hourTicks = hourStep >= 24 ? [] : idx.filter((i) => {
    const d = new Date(t[i] * 1000);
    return d.getMinutes() === 0 && d.getHours() % hourStep === 0;
  });

  function pointerToIdx(clientX: number): number | null {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = clientX;
    pt.y = 0;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const vx = pt.matrixTransform(ctm.inverse()).x;
    const ts = t[first] + ((vx - PAD.left) / innerW) * span;
    let best = idx[0];
    for (const i of idx) if (Math.abs(t[i] - ts) < Math.abs(t[best] - ts)) best = i;
    return best;
  }
  function scrub(clientX: number, isTouch: boolean) {
    const i = pointerToIdx(clientX);
    if (i == null) return;
    setHoverIdx(i);
    if (isTouch && i !== lastVibrated.current && "vibrate" in navigator) {
      navigator.vibrate(3);
      lastVibrated.current = i;
    }
  }

  function Badge({ i, above }: { i: number; above: boolean }) {
    const p = prices[i];
    if (p == null) return null;
    const bx = Math.min(
      VB_W - PAD.right - 54,
      Math.max(PAD.left + 54, x(t[i] + stepSec / 2)),
    );
    const by = above
      ? Math.max(PAD.top - 14, y(p * 100) - 26)
      : Math.min(VB_H - PAD.bottom - 10, y(p * 100) + 14);
    return (
      <g>
        <rect
          x={bx - 52}
          y={by - 14}
          width={104}
          height={28}
          rx={14}
          fill="var(--color-card-soft, #1f2937)"
          stroke="var(--color-line)"
          strokeWidth={1}
        />
        <text
          x={bx}
          y={by}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={17}
          fontWeight={700}
          fill="var(--color-ink)"
          className="tabular"
        >
          {fmtCt(p)}
        </text>
      </g>
    );
  }

  const nowP = prices[currentIdx];

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="w-full select-none"
      style={{ touchAction: "pan-y" }}
      data-swipe-consume="true"
      role="img"
      aria-label={forecastFrom != null ? "Stroomprijs, bekend en verwacht" : "Stroomprijs vandaag en morgen"}
      onMouseMove={(e) => scrub(e.clientX, false)}
      onMouseLeave={() => setHoverIdx(null)}
      onTouchStart={(e) => scrub(e.touches[0].clientX, true)}
      onTouchMove={(e) => scrub(e.touches[0].clientX, true)}
      onTouchEnd={() => setHoverIdx(null)}
      onTouchCancel={() => setHoverIdx(null)}
    >
      {/* ct gridlines every 10 */}
      {Array.from(
        { length: Math.floor((top - bottom) / 10) + 1 },
        (_, k) => bottom + k * 10,
      ).map((ct) => (
        <g key={ct}>
          <line
            x1={PAD.left}
            x2={VB_W - PAD.right}
            y1={y(ct)}
            y2={y(ct)}
            stroke="var(--color-line)"
            strokeWidth={1}
            strokeDasharray={ct === 0 ? undefined : "3 5"}
          />
          <text
            x={PAD.left - 6}
            y={y(ct)}
            textAnchor="end"
            dominantBaseline="central"
            fontSize={10}
            fill="var(--color-ink-mute)"
            className="tabular"
          >
            {ct}
          </text>
        </g>
      ))}
      <text x={PAD.left - 6} y={PAD.top - 14} textAnchor="end" fontSize={8}
        fill="var(--color-ink-mute)">ct</text>

      {/* day boundaries */}
      {days.map((d) => (
        <g key={d.ts}>
          <line
            x1={x(d.ts)}
            x2={x(d.ts)}
            y1={PAD.top - 8}
            y2={VB_H - PAD.bottom}
            stroke="var(--color-line)"
            strokeWidth={1}
          />
          <text
            x={x(d.ts) + 5}
            y={PAD.top - 12}
            fontSize={10}
            fill="var(--color-ink-mute)"
          >
            {d.label}
          </text>
        </g>
      ))}

      {/* the sell price, muted, under the buy line */}
      {secSegs.length > 0 && (
        <path
          d={secSegs.join(" ")}
          fill="none"
          stroke="var(--color-ink-mute)"
          strokeWidth={1.6}
          strokeOpacity={0.55}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      )}

      {/* the stepped price line - colored by cost tier */}
      {tierSegs.map((segs, tier) =>
        segs.length ? (
          <path
            key={tier}
            d={segs.join(" ")}
            fill="none"
            stroke={tierColor[tier]}
            strokeWidth={2.2}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null,
      )}
      {/* the AI forecast: same tiers, lighter */}
      {fcSegs.map((segs, tier) =>
        segs.length ? (
          <path
            key={`fc${tier}`}
            d={segs.join(" ")}
            fill="none"
            stroke={tierColor[tier]}
            strokeWidth={2.2}
            strokeOpacity={0.45}
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        ) : null,
      )}
      {forecastFrom != null && forecastFrom > t[first] && forecastFrom <= t[last] && (
        <g>
          <line x1={x(forecastFrom)} x2={x(forecastFrom)} y1={PAD.top} y2={VB_H - PAD.bottom}
            stroke="var(--color-price-high, #ea580c)" strokeWidth={1} strokeDasharray="4 3" opacity={0.6} />
          <text x={x(forecastFrom) + 4} y={PAD.top + 2} fontSize={8.5} fill="var(--color-ink-mute)">
            verwachting →
          </text>
        </g>
      )}

      {/* now-dot */}
      {nowP != null && (
        <circle
          cx={x(t[currentIdx] + stepSec / 2)}
          cy={y(nowP * 100)}
          r={5}
          fill="var(--color-card)"
          stroke={tierColor[tierOf(nowP)]}
          strokeWidth={2.5}
        />
      )}

      {/* min / max badges - verborgen tijdens scrubben: de
          scrub-uitlezing botste er letterlijk overheen (10-09) en
          tijdens het scrubben IS de uitlezing het antwoord. */}
      {hoverIdx == null && maxI !== minI && <Badge i={maxI} above={true} />}
      {hoverIdx == null && <Badge i={minI} above={false} />}

      {/* scrub readout */}
      {hoverIdx != null && prices[hoverIdx] != null && (
        <g>
          <line
            x1={x(t[hoverIdx] + stepSec / 2)}
            x2={x(t[hoverIdx] + stepSec / 2)}
            y1={PAD.top}
            y2={VB_H - PAD.bottom}
            stroke="var(--color-ink-mute)"
            strokeWidth={1}
            strokeDasharray="2 3"
          />
          <text
            x={VB_W - PAD.right}
            y={PAD.top - 10}
            textAnchor="end"
            fontSize={17}
            fontWeight={700}
            fill="var(--color-ink)"
            className="tabular"
          >
            {multiDay && (
              new Date(t[hoverIdx] * 1000).toLocaleDateString("nl-NL", { weekday: "short" }) + " "
            )}
            {new Date(t[hoverIdx] * 1000).toLocaleTimeString("nl-NL", {
              hour: "2-digit",
              minute: "2-digit",
            })}
            {" · "}
            {fmtCt(prices[hoverIdx] as number)}
          </text>
          {secondary?.[hoverIdx] != null && (
            <text
              x={VB_W - PAD.right}
              y={PAD.top + 6}
              textAnchor="end"
              fontSize={11}
              fill="var(--color-ink-mute)"
              className="tabular"
            >
              {secondaryLabel} {fmtCt(secondary[hoverIdx] as number)}
            </text>
          )}
        </g>
      )}

      {/* x labels every 3 h */}
      {hourTicks.map((i) => (
        <text
          key={t[i]}
          x={x(t[i])}
          y={VB_H - 8}
          textAnchor="middle"
          fontSize={10}
          fill="var(--color-ink-mute)"
          className="tabular"
        >
          {new Date(t[i] * 1000).getHours().toString().padStart(2, "0")}
        </text>
      ))}
    </svg>
  );
}
